import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getFirestore, doc, getDoc, getDocs, setDoc, updateDoc, collection, query, where, limit, startAfter, orderBy,
  increment, arrayUnion, arrayRemove, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Header from '../components/Header';

import Swal from 'sweetalert2';
import './StorePage.css';

const db = getFirestore();
const functions = getFunctions();

const fontMap = {
  modern: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  classic: "'Georgia', 'Times New Roman', serif",
  playful: "'Comic Sans MS', 'Chalkboard SE', sans-serif",
};

const borderRadiusMap = {
  none: '0px',
  small: '4px',
  large: '16px',
};

const StorePage = () => {
  const { slug } = useParams();
  const storeId = slug ? slug.split('-').pop() : '';
  const navigate = useNavigate();
  const location = useLocation();
  // Reservation mode: initialized from navigation state (explicit "Add a
  // pre-order from the menu" flow), but can ALSO be activated here when the
  // auto-detection banner finds an active reservation at this store and the
  // user accepts. Browsing alone NEVER auto-associates — the user decides.
  const [reservationContext, setReservationContext] = useState(location.state?.reservationContext || null);
  const [activeReservation, setActiveReservation] = useState(null);
  const [reservationBannerDismissed, setReservationBannerDismissed] = useState(false);
  const { user, userData, guestLocation, hasLocation, loading: authLoading, isFavorite, addRemoveFavorite } = useAuth();

  const [store, setStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [reviewStats, setReviewStats] = useState({ averageRating: 0, totalReviews: 0 });
  const [storeReviews, setStoreReviews] = useState([]);
  const [allStoreReviews, setAllStoreReviews] = useState([]);
  const [storeReviewHasMore, setStoreReviewHasMore] = useState(false);
  const [storeReviewLoading, setStoreReviewLoading] = useState(false);
  const [productRatings, setProductRatings] = useState({});
  const [selectedProductReviews, setSelectedProductReviews] = useState([]);
  const [selectedProductReviewHasMore, setSelectedProductReviewHasMore] = useState(false);
  const [selectedProductReviewLoading, setSelectedProductReviewLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Cart
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem(`ff_cart_${storeId}`);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);

  // Product Options Modal
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [modalQty, setModalQty] = useState(1);
  const [modalInstructions, setModalInstructions] = useState('');

  // Favorite status (from AuthContext which reads Firestore cache)
  const [isFavoriteState, setIsFavoriteState] = useState(() => isFavorite(storeId));

  // Refs for category sections (scrollspy)
  const categoryRefs = useRef({});
  const menuContainerRef = useRef(null);
  const categoryBarRef = useRef(null);
  const titleRef = useRef(null);
  const titleWrapRef = useRef(null);
  const productReviewsScrollRef = useRef(null);
  const lastProductReviewDoc = useRef(null);
  const storeReviewsScrollRef = useRef(null);

  useEffect(() => {
    if (authLoading) return; // wait for auth/location state to hydrate
    if (!hasLocation()) {
      navigate('/');
      return;
    }
    loadStoreData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, authLoading]);

  // Auto-detect an ACTIVE reservation at THIS store (pending/confirmed,
  // upcoming) and offer — via a dismissable banner — to add the cart to it.
  // Pure navigation + detection: browsing never auto-associates.
  useEffect(() => {
    if (authLoading || !user || reservationContext) return;
    const check = async () => {
      try {
        const q = query(
          collection(db, 'users', user.uid, 'reservations'),
          where('locationId', '==', storeId),
          where('dateTime', '>=', Timestamp.now()),
          orderBy('dateTime', 'asc'),
          limit(10)
        );
        const snap = await getDocs(q);
        for (const d of snap.docs) {
          const data = d.data();
          if (['pending', 'confirmed'].includes(data.status)) {
            setActiveReservation({ id: d.id, ...data });
            return;
          }
        }
      } catch (e) {
        console.warn('Active reservation check failed (ignored):', e);
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, storeId, reservationContext]);

  const acceptReservationBanner = () => {
    if (!activeReservation) return;
    setReservationContext({
      reservationId: activeReservation.id,
      businessId: activeReservation.businessId,
      locationId: activeReservation.locationId,
      locationName: activeReservation.locationName,
      dateTime: activeReservation.dateTime?.toDate?.()?.toISOString() || null,
      partySize: activeReservation.partySize,
    });
  };

  // Sync favorite status from AuthContext whenever storeId or favorites change
  useEffect(() => {
    setIsFavoriteState(isFavorite(storeId));
  }, [storeId, isFavorite]);

  // Scrollspy: update activeCategory based on scroll position
  useEffect(() => {
    if (categories.length === 0) return;
    const handleScroll = () => {
      const headerOffset = 120; // sticky header + category bar
      const scrollY = window.scrollY + headerOffset + 20;
      let bestCatId = categories[0]?.id || '__deals__';
      let minDist = Infinity;
      for (const cat of categories) {
        const el = categoryRefs.current[cat.id];
        if (!el) continue;
        const dist = Math.abs(el.offsetTop - scrollY);
        if (dist < minDist) {
          minDist = dist;
          bestCatId = cat.id;
        }
      }
      // Also check deals section if it exists
      const dealsEl = document.getElementById('ff-deals-section');
      if (dealsEl) {
        const dealsDist = Math.abs(dealsEl.offsetTop - scrollY);
        if (dealsDist < minDist) {
          bestCatId = '__deals__';
        }
      }
      setActiveCategory(bestCatId);
    };
    const container = menuContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [categories, products]);

  // Auto-scroll category bar horizontally so the active pill is always visible
  useEffect(() => {
    const bar = categoryBarRef.current;
    if (!bar || !activeCategory) return;
    // Find the active pill button inside the bar
    const activeBtn = bar.querySelector(`button[data-cat-id="${activeCategory}"]`);
    if (!activeBtn) return;
    const barRect = bar.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();
    const pad = 16; // padding so it's not flush against the edge
    // If the button is partially or fully outside the visible bar area, scroll it into view
    if (btnRect.left < barRect.left + pad) {
      bar.scrollTo({ left: bar.scrollLeft + (btnRect.left - barRect.left) - pad, behavior: 'smooth' });
    } else if (btnRect.right > barRect.right - pad) {
      bar.scrollTo({ left: bar.scrollLeft + (btnRect.right - barRect.right) + pad, behavior: 'smooth' });
    }
  }, [activeCategory]);

  // Resize restaurant title to fit one line
  useEffect(() => {
    const el = titleRef.current;
    const wrap = titleWrapRef.current;
    if (!el || !wrap || !store) return;

    const resize = () => {
      // Reset to default first so we measure the natural width
      el.style.fontSize = '';
      // Temporarily remove ellipsis so scrollWidth reports full text width
      el.style.overflow = 'visible';
      el.style.textOverflow = 'clip';

      const wrapWidth = wrap.clientWidth;
      const textWidth = el.scrollWidth;

      if (textWidth > wrapWidth && wrapWidth > 0) {
        const baseSize = parseFloat(getComputedStyle(el).fontSize);
        const ratio = wrapWidth / textWidth;
        const newSize = Math.max(baseSize * ratio * 0.92, 11); // 11px minimum, 8% safety buffer
        el.style.fontSize = `${newSize}px`;
      }

      // Restore ellipsis styling
      el.style.overflow = 'hidden';
      el.style.textOverflow = 'ellipsis';
    };

    // Run after layout paint
    requestAnimationFrame(resize);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [store?.locationName, store]);

  useEffect(() => {
    try {
      localStorage.setItem(`ff_cart_${storeId}`, JSON.stringify(cart));
    } catch {
      // ignore
    }
  }, [cart, storeId]);

  const loadStoreData = async () => {
    setLoading(true);
    try {
      const storeDoc = await getDoc(doc(db, 'publicStores', storeId));
      if (!storeDoc.exists()) {
        navigate('/explore');
        return;
      }
      const storeData = { id: storeDoc.id, ...storeDoc.data() };
      setStore(storeData);

      const catsSnap = await getDocs(collection(db, 'publicStores', storeId, 'categories'));
      const cats = [];
      const prods = [];
      for (const catDoc of catsSnap.docs) {
        const cat = { id: catDoc.id, ...catDoc.data() };
        cats.push(cat);
        const prodSnap = await getDocs(
          collection(db, 'publicStores', storeId, 'categories', cat.id, 'products')
        );
        prodSnap.docs.forEach((p) => {
          const pdata = p.data();
          // Availability + stock gates (inventory module, opt-in): hidden or
          // tracked-out products don't sell online either.
          if (pdata.available === false) return;
          // Mode-aware stock gate (mirrors the backend depletion resolution):
          // recipe-mode products rely on `available` (backend auto-86s when
          // ingredients run out); stock-mode gate on units — per-location
          // bucket (this store IS the location) vs global pool.
          const invMode = (pdata.inventoryMode === 'stock' || pdata.inventoryMode === 'recipe')
            ? pdata.inventoryMode
            : ((pdata.recipe || []).length > 0 ? 'recipe' : (pdata.trackStock === true ? 'stock' : null));
          if (invMode === 'stock') {
            if (pdata.stockScope === 'byLocation') {
              const m = pdata.stockByLocation || {};
              if ((Number(m[storeId]) || 0) <= 0) return;
            } else if ((Number(pdata.stockQty) || 0) <= 0) return;
          }
          prods.push({ id: p.id, categoryId: cat.id, ...pdata });
        });
      }
      // Firestore returns docs in arbitrary (doc-id) order. The admin apps
      // order categories/products by their `order` field — honor it so the
      // store page matches the merchant's arrangement. Products sort once
      // here; the per-category filter below preserves that order.
      cats.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      prods.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setCategories(cats);
      setProducts(prods);

      setActiveCategory('__all__');

      const optsSnap = await getDocs(collection(db, 'publicStores', storeId, 'productOptions'));
      const opts = optsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProductOptions(opts);

      // Load promotions
      const promosSnap = await getDocs(collection(db, 'publicStores', storeId, 'promotions'));
      const now = new Date();
      const promos = promosSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((p) => {
        if (p.active === false) return false;
        if (p.startDate && p.startDate.toDate && p.startDate.toDate() > now) return false;
        if (p.endDate && p.endDate.toDate && p.endDate.toDate() < now) return false;
        return true;
      });
      setPromotions(promos);

      // Load review stats
      const statsDoc = await getDoc(doc(db, 'publicStores', storeId, 'reviewStats', 'summary'));
      if (statsDoc.exists()) {
        const s = statsDoc.data();
        setReviewStats({ averageRating: s.averageRating || 0, totalReviews: s.totalReviews || 0 });
      }

      // Load product ratings map
      const prodStatsSnap = await getDocs(collection(db, 'publicStores', storeId, 'productReviewStats'));
      const prMap = {};
      prodStatsSnap.docs.forEach((d) => {
        const data = d.data();
        prMap[d.id] = { averageRating: data.averageRating || 0, totalReviews: data.totalReviews || 0 };
      });
      setProductRatings(prMap);

      // Load store reviews (all, ordered by date desc)
      const reviewsSnap = await getDocs(collection(db, 'publicStores', storeId, 'reviews'));
      const revs = reviewsSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return bTime - aTime;
      });
      setAllStoreReviews(revs);
      setStoreReviews(revs.slice(0, 5));
      setStoreReviewHasMore(revs.length > 5);
    } catch (error) {
      console.error('Error loading store:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    const num = typeof val === 'number' ? val : parseFloat(val) || 0;
    return `$${num.toFixed(2)}`;
  };

  const getProductOptions = (product) => {
    const ids = product.optionIds || [];
    return productOptions.filter((o) => ids.includes(o.id));
  };

  const openProductModal = async (product) => {
    setSelectedProduct(product);
    setSelectedOptions([]);
    setModalQty(1);
    setModalInstructions('');
    setSelectedProductReviews([]);
    setSelectedProductReviewHasMore(false);
    lastProductReviewDoc.current = null;
    try {
      const q = query(
        collection(db, 'publicStores', storeId, 'productReviews'),
        where('productId', '==', product.id),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const revSnap = await getDocs(q);
      const revs = revSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSelectedProductReviews(revs);
      setSelectedProductReviewHasMore(revs.length === 5);
      lastProductReviewDoc.current = revSnap.docs[revs.length - 1] || null;
    } catch (e) {
      console.warn('Failed to load product reviews:', e);
    }
  };

  const loadMoreProductReviews = async () => {
    if (!selectedProduct || selectedProductReviewLoading || !selectedProductReviewHasMore) return;
    if (!lastProductReviewDoc.current) return;
    setSelectedProductReviewLoading(true);
    try {
      const q = query(
        collection(db, 'publicStores', storeId, 'productReviews'),
        where('productId', '==', selectedProduct.id),
        orderBy('createdAt', 'desc'),
        startAfter(lastProductReviewDoc.current),
        limit(5)
      );
      const revSnap = await getDocs(q);
      const newOnes = revSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSelectedProductReviews((prev) => [...prev, ...newOnes]);
      lastProductReviewDoc.current = revSnap.docs[newOnes.length - 1] || lastProductReviewDoc.current;
      setSelectedProductReviewHasMore(newOnes.length === 5);
    } catch (e) {
      console.warn('Failed to load more product reviews:', e);
    } finally {
      setSelectedProductReviewLoading(false);
    }
  };

  const loadMoreStoreReviews = () => {
    if (storeReviewLoading || !storeReviewHasMore) return;
    setStoreReviewLoading(true);
    const nextBatch = allStoreReviews.slice(storeReviews.length, storeReviews.length + 5);
    setStoreReviews((prev) => [...prev, ...nextBatch]);
    setStoreReviewHasMore(allStoreReviews.length > storeReviews.length + nextBatch.length);
    setStoreReviewLoading(false);
  };

  const toggleOptionChoice = (optionId, choiceIndex, allowMultiple) => {
    setSelectedOptions((prev) => {
      const existing = prev.filter((o) => o.optionId === optionId);
      const others = prev.filter((o) => o.optionId !== optionId);
      const hasThis = existing.some((o) => o.choiceIndex === choiceIndex);

      if (allowMultiple) {
        if (hasThis) {
          return others.concat(existing.filter((o) => o.choiceIndex !== choiceIndex));
        }
        return others.concat([...existing, { optionId, choiceIndex }]);
      }
      return others.concat({ optionId, choiceIndex });
    });
  };

  const isChoiceSelected = (optionId, choiceIndex) =>
    selectedOptions.some((o) => o.optionId === optionId && o.choiceIndex === choiceIndex);

  const getModalItemTotal = () => {
    if (!selectedProduct) return 0;
    let base = parseFloat(selectedProduct.price) || 0;
    selectedOptions.forEach((sel) => {
      const optDef = productOptions.find((o) => o.id === sel.optionId);
      if (optDef && optDef.options && optDef.options[sel.choiceIndex]) {
        base += parseFloat(optDef.options[sel.choiceIndex].extraFee) || 0;
      }
    });
    return base * modalQty;
  };

  // Walk-in queue: join the waitlist with a live estimate preview.
  const handleJoinWaitlist = async () => {
    const { value: formValues } = await Swal.fire({
      title: 'Join the waitlist',
      html: `
        <input id="swal-wl-name" class="swal2-input" placeholder="Name *" value="${(userData?.name || user?.displayName || '').replace(/"/g, '&quot;')}">
        <input id="swal-wl-phone" class="swal2-input" placeholder="Phone (for SMS when ready)" value="${(userData?.phone || '').replace(/"/g, '&quot;')}">
        <input id="swal-wl-party" class="swal2-input" type="number" min="1" value="2" placeholder="Party size *">
        <div id="swal-wl-estimate" style="margin-top:8px;"></div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Check estimated wait',
      confirmButtonColor: '#ff5722',
      showLoaderOnConfirm: true,
      didOpen: () => {
        // Live estimate on party-size change
        document.getElementById('swal-wl-party').addEventListener('change', async (e) => {
          const est = document.getElementById('swal-wl-estimate');
          est.innerHTML = '<em style="color:#6b7280;">Estimating…</em>';
          try {
            const fn = httpsCallable(functions, 'getQueueEstimate');
            const { data } = await fn({
              businessId: store.businessId,
              locationId: storeId,
              partySize: parseInt(e.target.value, 10) || 2,
            });
            est.innerHTML = data.enabled === false
              ? ''
              : `<div style="background:#fff3ec;border-radius:10px;padding:10px;">
                   <strong style="color:#ff5722;">~${data.estimatedWaitMinutes} min wait</strong>
                   <span style="color:#6b7280;font-size:12px;"> · ${data.partiesAhead} ${data.partiesAhead === 1 ? 'party' : 'parties'} ahead</span>
                 </div>`;
          } catch (err) {
            est.innerHTML = '';
          }
        });
      },
      preConfirm: async () => {
        const name = document.getElementById('swal-wl-name').value.trim();
        const phone = document.getElementById('swal-wl-phone').value.trim();
        const party = parseInt(document.getElementById('swal-wl-party').value, 10);
        if (!name) return Swal.showValidationMessage('Name is required');
        if (!party || party < 1) return Swal.showValidationMessage('Party size is required');
        try {
          const fn = httpsCallable(functions, 'joinWaitlist');
          const { data } = await fn({
            businessId: store.businessId,
            locationId: storeId,
            customerName: name,
            customerPhone: phone || null,
            partySize: party,
            source: 'public_web',
          });
          if (!data.success) throw new Error(data.error || 'Could not join');
          return data;
        } catch (e) {
          return Swal.showValidationMessage(e.message || 'Could not join the waitlist');
        }
      },
    });
    if (formValues?.entryId) {
      navigate(`/queue/${formValues.entryId}?b=${store.businessId}&l=${storeId}`);
    }
  };

  const addToCart = (product, options, qty, instructions) => {
    setCart((prev) => {
      const existingIndex = prev.findIndex((item) => {
        if (item.product.id !== product.id) return false;
        if ((item.instructions || '') !== (instructions || '')) return false;
        if (item.options.length !== options.length) return false;
        const a = [...item.options].sort((x, y) => `${x.optionId}-${x.choiceIndex}`.localeCompare(`${y.optionId}-${y.choiceIndex}`));
        const b = [...options].sort((x, y) => `${x.optionId}-${x.choiceIndex}`.localeCompare(`${y.optionId}-${y.choiceIndex}`));
        return a.every((v, i) => v.optionId === b[i].optionId && v.choiceIndex === b[i].choiceIndex);
      });

      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = { ...next[existingIndex], qty: next[existingIndex].qty + qty };
        return next;
      }
      return [...prev, { product, options, qty, instructions }];
    });
    setSelectedProduct(null);
    setCartOpen(true);
  };

  const updateCartQty = (index, delta) => {
    setCart((prev) => {
      const next = [...prev];
      const newQty = (next[index].qty || 1) + delta;
      if (newQty <= 0) {
        next.splice(index, 1);
      } else {
        next[index] = { ...next[index], qty: newQty };
      }
      return next;
    });
  };

  const removeCartItem = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const cartTotals = useMemo(() => {
    let subtotal = 0;
    cart.forEach((item) => {
      let unit = parseFloat(item.product.price) || 0;
      item.options.forEach((sel) => {
        const optDef = productOptions.find((o) => o.id === sel.optionId);
        if (optDef && optDef.options && optDef.options[sel.choiceIndex]) {
          unit += parseFloat(optDef.options[sel.choiceIndex].extraFee) || 0;
        }
      });
      subtotal += unit * item.qty;
    });
    const taxRate = store?.taxRate || 0;
    const tax = subtotal * (taxRate / 100);
    const total = subtotal + tax;
    return { subtotal, tax, total, taxRate };
  }, [cart, productOptions, store]);

  const handleToggleFavorite = async () => {
    try {
      const result = await addRemoveFavorite(storeId, store || {});
      setIsFavoriteState(result.isFavorite);
      // Update global favorite counter
      const storeRef = doc(db, 'publicStores', storeId);
      await updateDoc(storeRef, {
        favoriteCount: increment(result.isFavorite ? 1 : -1),
      });
      setStore((prev) => prev ? { ...prev, favoriteCount: Math.max(0, (prev.favoriteCount || 0) + (result.isFavorite ? 1 : -1)) } : prev);
    } catch (e) {
      console.error('Error toggling favorite:', e);
    }
  };

  // Design helpers
  const design = store?.design || {};
  const primaryColor = design.primaryColor || '#ff5722';
  const secondaryColor = design.secondaryColor || '#ff8a50';
  const fontFamily = fontMap[design.fontFamily] || fontMap.modern;
  const br = borderRadiusMap[design.borderRadius] || borderRadiusMap.large;
  const headerTextColor = design.headerTextColor || '#fff';
  const productLayout = design.productLayout || 'grid';
  const showProductImages = design.showProductImages !== false;
  const template = design.template || 'modern';

  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  const handleHeaderCartClick = () => {
    setCartOpen(true);
  };

  if (loading) {
    return (
      <div className="ff-store-page" style={{ fontFamily }}>
        <Header cartCount={cartCount} onCartClick={handleHeaderCartClick} />
        <div className="ff-loading-stores">
          <div className="ff-spinner"></div>
          <p>Loading menu...</p>
        </div>
      </div>
    );
  }

  if (!store) return null;

  const headerPadding =
    design.headerStyle === 'minimal' ? '16px 0' : design.headerStyle === 'compact' ? '24px 0' : '48px 0';

  const logoRadius = template === 'bistro' ? '50%' : '16px';
  // Logo should be at least as tall as the three text lines (title + address + phone)
  const logoSize = template === 'bistro' ? 140 : 120;

  return (
    <div className="ff-store-page" style={{ backgroundColor: design.backgroundColor || '#f8f9fa', color: design.textColor || '#333', fontFamily }}>
      <Header cartCount={cartCount} onCartClick={handleHeaderCartClick} />

      {/* Store Header */}
      {design.headerImageUrl ? (
        <div
          className="ff-store-header"
          style={{
            background: `url(${design.headerImageUrl}) center/cover`,
            color: headerTextColor,
            padding: headerPadding,
            textAlign: design.logoPosition === 'center' ? 'center' : design.logoPosition === 'right' ? 'right' : 'left',
          }}
        >
          <div className="container">
            <div
              className={`ff-store-header-inner logo-${design.logoPosition || 'center'}`}
              style={{
                alignItems: ['left', 'right'].includes(design.logoPosition) ? 'center' : (design.logoPosition === 'center' ? 'center' : 'flex-start'),
                flexDirection: ['left', 'right'].includes(design.logoPosition) ? 'row' : 'column',
                gap: ['left', 'right'].includes(design.logoPosition) ? 20 : 16,
                justifyContent: ['left', 'right'].includes(design.logoPosition)
                  ? (design.logoPosition === 'right' ? 'flex-end' : 'flex-start')
                  : 'center',
              }}
            >
              {(design.logoUrl || store.logoUrl) && (
                <img
                  src={design.logoUrl || store.logoUrl}
                  alt={store.locationName}
                  className="ff-store-logo"
                  style={{ borderRadius: logoRadius, height: logoSize, order: design.logoPosition === 'right' ? 2 : 0 }}
                />
              )}
              <div style={{ flex: 1, order: design.logoPosition === 'right' ? 1 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div ref={titleWrapRef} style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <h1
                      ref={titleRef}
                      className="ff-store-title"
                      style={{
                        fontWeight: template === 'classic' ? 400 : 700,
                        letterSpacing: template === 'classic' ? '1px' : 'normal',
                        fontSize: design.headerStyle === 'compact' ? '1.5rem' : '2.2rem',
                        margin: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {store.locationName}
                    </h1>
                  </div>
                  <button
                    onClick={handleToggleFavorite}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '20px',
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '0 14px',
                      cursor: 'pointer',
                      color: isFavoriteState ? '#ff1744' : headerTextColor,
                      fontSize: '1.1rem',
                      backdropFilter: 'blur(4px)',
                    }}
                    title={isFavoriteState ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <i className={`${isFavoriteState ? 'fas' : 'far'} fa-heart`}></i>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{store.favoriteCount || 0}</span>
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, marginBottom: 2, flexWrap: 'wrap' }}>
                  {reviewStats.averageRating > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'flex', gap: 2 }}>
                        {[1, 2, 3, 4, 5].map((s) => (
                          <i
                            key={s}
                            className={`fas fa-star${s <= Math.round(reviewStats.averageRating) ? '' : '-half-alt'}`}
                            style={{ color: s <= Math.round(reviewStats.averageRating) ? '#ffc107' : '#e0e0e0', fontSize: '0.85rem' }}
                          ></i>
                        ))}
                      </span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: headerTextColor, opacity: 0.9 }}>
                        {reviewStats.averageRating.toFixed(1)} ({reviewStats.totalReviews} reviews)
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <i className="fas fa-heart" style={{ fontSize: '0.8rem', color: headerTextColor, opacity: 0.7 }}></i>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: headerTextColor, opacity: 0.9 }}>
                      {store.favoriteCount || 0}
                    </span>
                  </div>
                </div>
                <p className="ff-store-meta" style={{ fontStyle: template === 'classic' ? 'italic' : 'normal', marginTop: 4 }}>
                  <i className="fas fa-map-marker-alt mr-1"></i>
                  {store.address || store.city}
                </p>
                {store.phone && (
                  <p className="ff-store-meta">
                    <i className="fas fa-phone mr-1"></i>
                    {store.phone}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="ff-store-header ff-store-hero-header" style={{ padding: headerPadding, textAlign: design.logoPosition === 'center' ? 'center' : design.logoPosition === 'right' ? 'right' : 'left' }}>
          <div className="ff-store-hero-ingredients">
            <div className="ff-ingredient tomato"></div>
            <div className="ff-ingredient leaf"></div>
            <div className="ff-ingredient garlic"></div>
            <div className="ff-ingredient lemon"></div>
            <div className="ff-ingredient strawberry"></div>
            <div className="ff-ingredient pepper"></div>
            <div className="ff-ingredient mushroom"></div>
            <div className="ff-ingredient carrot"></div>
            <div className="ff-ingredient banana"></div>
            <div className="ff-ingredient egg"></div>
            <div className="ff-ingredient sausage"></div>
            <div className="ff-ingredient cheese"></div>
            <div className="ff-ingredient broccoli"></div>
            <div className="ff-ingredient onion"></div>
            <div className="ff-ingredient bread"></div>
          </div>
          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <div
              className={`ff-store-header-inner logo-${design.logoPosition || 'center'}`}
              style={{
                alignItems: ['left', 'right'].includes(design.logoPosition) ? 'center' : (design.logoPosition === 'center' ? 'center' : 'flex-start'),
                flexDirection: ['left', 'right'].includes(design.logoPosition) ? 'row' : 'column',
                gap: ['left', 'right'].includes(design.logoPosition) ? 20 : 16,
                justifyContent: ['left', 'right'].includes(design.logoPosition)
                  ? (design.logoPosition === 'right' ? 'flex-end' : 'flex-start')
                  : 'center',
              }}
            >
              {(design.logoUrl || store.logoUrl) && (
                <img
                  src={design.logoUrl || store.logoUrl}
                  alt={store.locationName}
                  className="ff-store-logo"
                  style={{ borderRadius: logoRadius, height: logoSize, order: design.logoPosition === 'right' ? 2 : 0 }}
                />
              )}
              <div style={{ flex: 1, order: design.logoPosition === 'right' ? 1 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div ref={titleWrapRef} style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <h1
                      ref={titleRef}
                      className="ff-store-title"
                      style={{
                        fontWeight: template === 'classic' ? 400 : 700,
                        letterSpacing: template === 'classic' ? '1px' : 'normal',
                        fontSize: design.headerStyle === 'compact' ? '1.5rem' : '2.2rem',
                        margin: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {store.locationName}
                    </h1>
                  </div>
                  <button
                    onClick={handleToggleFavorite}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '20px',
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '0 14px',
                      cursor: 'pointer',
                      color: isFavoriteState ? '#ff1744' : '#fff',
                      fontSize: '1.1rem',
                      backdropFilter: 'blur(4px)',
                    }}
                    title={isFavoriteState ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <i className={`${isFavoriteState ? 'fas' : 'far'} fa-heart`}></i>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{store.favoriteCount || 0}</span>
                  </button>
                </div>
                {reviewStats.averageRating > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, marginBottom: 2 }}>
                    <span style={{ display: 'flex', gap: 2 }}>
                      {[1, 2, 3, 4, 5].map((s) => (
                        <i
                          key={s}
                          className={`fas fa-star${s <= Math.round(reviewStats.averageRating) ? '' : '-half-alt'}`}
                          style={{ color: s <= Math.round(reviewStats.averageRating) ? '#ffc107' : 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}
                        ></i>
                      ))}
                    </span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#fff', opacity: 0.9 }}>
                      {reviewStats.averageRating.toFixed(1)} ({reviewStats.totalReviews} reviews)
                    </span>
                  </div>
                )}
                <p className="ff-store-meta" style={{ fontStyle: template === 'classic' ? 'italic' : 'normal', marginTop: 4, color: '#fff', opacity: 0.9 }}>
                  <i className="fas fa-map-marker-alt mr-1"></i>
                  {store.address || store.city}
                </p>
                {store.phone && (
                  <p className="ff-store-meta" style={{ color: '#fff', opacity: 0.9 }}>
                    <i className="fas fa-phone mr-1"></i>
                    {store.phone}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cover Photos Carousel */}
      {store.coverImages && store.coverImages.length > 0 && (
        <CoverPhotoCarousel
          storeId={storeId}
          images={store.coverImages}
          user={user}
        />
      )}

      {/* Reservation banners moved INSIDE the menu container, pinned right
          below the sticky categories bar (see below) for maximum visibility. */}

      {/* Walk-in queue: Join waitlist */}
      {store?.queueSettings?.enabled === true && !reservationContext && (
        <div className="container" style={{ marginTop: 16 }}>
          <div
            style={{
              background: '#fff3ec',
              border: '1px solid rgba(255,87,34,0.3)',
              borderRadius: 12,
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              cursor: 'pointer',
              fontFamily,
            }}
            onClick={handleJoinWaitlist}
          >
            <div style={{
              background: 'rgba(255,87,34,0.15)',
              borderRadius: 10,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <i className="fas fa-list-ol" style={{ color: '#ff5722', fontSize: 18 }}></i>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Join the waitlist</div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                See your estimated wait and get texted when your table is ready
              </div>
            </div>
            <i className="fas fa-chevron-right" style={{ color: '#ff5722' }}></i>
          </div>
        </div>
      )}

      {/* Promotions Banner */}
      {promotions.length > 0 && (
        <div className="container" style={{ marginTop: 16, marginBottom: 8 }}>
          <h4 style={{
            fontFamily,
            fontWeight: 700,
            fontSize: '1rem',
            color: design.textColor || '#333',
            marginBottom: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <i className="fas fa-stopwatch" style={{ color: primaryColor }}></i>
            Limited Time Promotions
          </h4>
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4 }}>
            {promotions.map((promo) => (
              <div
                key={promo.id}
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                  color: '#fff',
                  borderRadius: br,
                  padding: '10px 16px',
                  minWidth: 180,
                  flexShrink: 0,
                  fontFamily,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{promo.name}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                  {promo.type === 'percentage' ? `${promo.value}% OFF` : `$${promo.value.toFixed(2)} OFF`}
                  {promo.minOrder > 0 && ` · Min $${promo.minOrder.toFixed(2)}`}
                </div>
                {promo.code && (
                  <div style={{ fontSize: '0.75rem', marginTop: 4, opacity: 0.85 }}>
                    Code: <strong>{promo.code}</strong>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Menu */}
      <div className="container ff-store-menu" ref={menuContainerRef}>
        {/* Category Nav — sticky horizontal scrollable */}
        <div className="ff-store-categories" style={{
          position: 'sticky',
          top: 60,
          zIndex: 100,
          background: '#fff',
          borderBottom: '1px solid #eee',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          padding: '6px 0',
        }}>
          <div ref={categoryBarRef} style={{ display: 'flex', gap: 8, overflowX: 'auto', flexWrap: 'nowrap' }}>
            {/* Deals first if there are any sale products */}
            {products.some((p) => p.onSale) && (
              <button
                data-cat-id="__deals__"
                className={`ff-store-pill ${activeCategory === '__deals__' ? 'active' : ''}`}
                onClick={() => {
                  const el = document.getElementById('ff-deals-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  setActiveCategory('__deals__');
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: template === 'bistro' ? 24 : 20,
                  border: 'none',
                  fontWeight: activeCategory === '__deals__' ? 700 : 400,
                  background: activeCategory === '__deals__' ? '#dc3545' : template === 'classic' ? '#f0f0f0' : '#f5f5f5',
                  color: activeCategory === '__deals__' ? '#fff' : (design.textColor || '#333'),
                  whiteSpace: 'nowrap',
                  fontFamily,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <i className="fas fa-tag mr-1"></i>Deals
              </button>
            )}
            {/* Real categories — scroll to section on click */}
            {categories.map((cat) => (
              <button
                key={cat.id}
                data-cat-id={cat.id}
                className={`ff-store-pill ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => {
                  const el = categoryRefs.current[cat.id];
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  setActiveCategory(cat.id);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: template === 'bistro' ? 24 : 20,
                  border: 'none',
                  fontWeight: activeCategory === cat.id ? 700 : 400,
                  background: activeCategory === cat.id ? primaryColor : template === 'classic' ? '#f0f0f0' : '#f5f5f5',
                  color: activeCategory === cat.id ? '#fff' : (design.textColor || '#333'),
                  whiteSpace: 'nowrap',
                  fontFamily,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Reservation banners — STICKY right below the categories bar
            (60px header + ~48px categories = top 108) so they stay visible
            while scrolling the menu. */}
        {(reservationContext || (activeReservation && !reservationBannerDismissed)) && (
          <div style={{
            position: 'sticky',
            top: 108,
            zIndex: 99,
            background: '#fff',
            padding: '8px 0',
          }}>
            {reservationContext ? (
              <div style={{
                background: '#fff3ec',
                border: '1px solid rgba(255,87,34,0.3)',
                borderRadius: 12,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontFamily,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
                <i className="fas fa-calendar-check" style={{ color: '#ff5722' }}></i>
                <span style={{ fontWeight: 600, fontSize: '0.9rem', flex: 1 }}>
                  Adding items to your reservation
                  {reservationContext.dateTime
                    ? ` — ${new Date(reservationContext.dateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${new Date(reservationContext.dateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                    : ''}
                </span>
                <button
                  onClick={() => setReservationContext(null)}
                  style={{ background: 'none', border: 'none', color: '#ff5722', cursor: 'pointer', padding: 4 }}
                  title="Stop ordering for this reservation"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ) : (
              <div style={{
                background: 'linear-gradient(135deg, #ff5722 0%, #ff8a50 100%)',
                borderRadius: 12,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontFamily,
                color: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}>
                <i className="fas fa-calendar-check" style={{ fontSize: 18 }}></i>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    You have a reservation at {activeReservation.dateTime?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {activeReservation.dateTime?.toDate?.()?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                    Want these items to be part of your reservation?
                  </div>
                </div>
                <button
                  onClick={acceptReservationBanner}
                  style={{
                    background: '#fff',
                    color: '#ff5722',
                    border: 'none',
                    borderRadius: 8,
                    padding: '6px 14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Yes
                </button>
                <button
                  onClick={() => setReservationBannerDismissed(true)}
                  style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4 }}
                  title="Dismiss"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Search Bar */}
        <div style={{ padding: '6px 12px', maxWidth: 500, margin: '0 auto' }}>
          <div className="ff-store-search-wrap">
            <i className="fas fa-search" style={{ color: '#999', fontSize: '1rem' }}></i>
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  padding: 0,
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        {/* Products grouped by category sections */}
        <div style={{ paddingTop: 16 }}>
          {/* Search Results */}
          {searchQuery && (
            <div style={{ marginBottom: 32 }}>
              <h3 style={{
                fontSize: '1.3rem',
                fontWeight: 700,
                color: design.textColor || '#333',
                marginBottom: 16,
                paddingBottom: 8,
                borderBottom: `2px solid ${primaryColor}`,
                fontFamily,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <i className="fas fa-search" style={{ fontSize: '0.9rem' }}></i>
                Search Results for "{searchQuery}"
              </h3>
              {(() => {
                const q = searchQuery.toLowerCase();
                const filtered = products.filter((p) =>
                  (p.name || '').toLowerCase().includes(q) ||
                  (p.description || '').toLowerCase().includes(q)
                );
                return filtered.length === 0 ? (
                  <p style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9rem' }}>No products found</p>
                ) : (
                  <ProductList
                    products={filtered}
                    productLayout={productLayout}
                    template={template}
                    design={design}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    br={br}
                    showProductImages={showProductImages}
                    fontFamily={fontFamily}
                    openProductModal={openProductModal}
                    formatCurrency={formatCurrency}
                    productRatings={productRatings}
                  />
                );
              })()}
            </div>
          )}

          {/* Deals section */}
          {!searchQuery && products.some((p) => p.onSale) && (
            <div
              id="ff-deals-section"
              data-category-id="__deals__"
              style={{ marginBottom: 32, scrollMarginTop: '76px' }}
            >
              <h3 style={{
                fontSize: '1.3rem',
                fontWeight: 700,
                color: '#dc3545',
                marginBottom: 16,
                paddingBottom: 8,
                borderBottom: '2px solid #dc3545',
                fontFamily,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc3545', display: 'inline-block' }}></span>
                Deals
              </h3>
              <ProductList
                products={products.filter((p) => p.onSale)}
                productLayout={productLayout}
                template={template}
                design={design}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                br={br}
                showProductImages={showProductImages}
                fontFamily={fontFamily}
                openProductModal={openProductModal}
                formatCurrency={formatCurrency}
                productRatings={productRatings}
              />
            </div>
          )}
          {/* Category sections */}
          {!searchQuery && categories.map((cat) => {
            const catProducts = products.filter((p) => p.categoryId === cat.id);
            return (
              <div
                key={cat.id}
                data-category-id={cat.id}
                ref={(el) => { categoryRefs.current[cat.id] = el; }}
                style={{ marginBottom: 32, minHeight: catProducts.length === 0 ? 60 : undefined, scrollMarginTop: '76px' }}
              >
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: 700,
                  color: design.textColor || '#333',
                  marginBottom: 16,
                  paddingBottom: 8,
                  borderBottom: `2px solid ${primaryColor}`,
                  fontFamily,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: primaryColor, display: 'inline-block' }}></span>
                  {cat.name}
                </h3>
                {catProducts.length === 0 ? (
                  <p style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9rem' }}>No products in this category</p>
                ) : (
                  <ProductList
                    products={catProducts}
                    productLayout={productLayout}
                    template={template}
                    design={design}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    br={br}
                    showProductImages={showProductImages}
                    fontFamily={fontFamily}
                    openProductModal={openProductModal}
                    formatCurrency={formatCurrency}
                    productRatings={productRatings}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Store Reviews Section — horizontal scrollable with lazy loading */}
      {storeReviews.length > 0 && (
        <div className="container" style={{ padding: '32px 12px', marginTop: 16 }}>
          <h3 style={{
            fontSize: '1.3rem',
            fontWeight: 700,
            color: design.textColor || '#333',
            marginBottom: 16,
            paddingBottom: 8,
            borderBottom: `2px solid ${primaryColor}`,
            fontFamily,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <i className="fas fa-comments" style={{ color: primaryColor }}></i>
            Reviews
          </h3>
          <div
            ref={storeReviewsScrollRef}
            onScroll={(e) => {
              const el = e.currentTarget;
              if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 20) {
                loadMoreStoreReviews();
              }
            }}
            style={{
              display: 'flex',
              gap: 12,
              overflowX: 'auto',
              paddingBottom: 8,
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {storeReviews.map((rev) => (
              <div
                key={rev.id}
                style={{
                  flex: '0 0 280px',
                  padding: '14px 16px',
                  background: '#fff',
                  borderRadius: br,
                  border: '1px solid #eee',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  scrollSnapAlign: 'start',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: primaryColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 700 }}>
                        {(rev.consumerName || 'C').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>{rev.consumerName || 'Customer'}</div>
                        <div style={{ fontSize: '0.75rem', color: '#888' }}>
                          {rev.createdAt?.toDate ? rev.createdAt.toDate().toLocaleDateString() : new Date(rev.createdAt || 0).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <StarDisplay rating={rev.rating} size={12} />
                  </div>
                  {rev.comment && (
                    <p style={{ fontSize: '0.85rem', color: '#555', margin: 0, lineHeight: 1.4 }}>{rev.comment}</p>
                  )}
                </div>
              </div>
            ))}
            {storeReviewLoading && (
              <div style={{ flex: '0 0 100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fas fa-spinner fa-spin" style={{ color: '#ccc', fontSize: '1rem' }}></i>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="ff-store-footer" style={{ background: design.headerStyle === 'banner' ? `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` : primaryColor, color: headerTextColor }}>
        <div className="container text-center">
          <img src="/fodyfee_logo.png" alt="FodyFee" style={{ height: 28, marginBottom: 8, filter: (headerTextColor).toLowerCase() === '#000000' || headerTextColor.toLowerCase() === '#000' ? 'invert(1)' : 'none' }} />
          <p style={{ opacity: 0.8, fontSize: '0.85rem', marginBottom: 4, fontFamily }}>
            <i className="fas fa-heart mr-1"></i>
            Made with love from Gilroy, California
          </p>
          <p style={{ opacity: 0.8, fontSize: '0.85rem', fontFamily }}>
            Powered by <a href="https://fodyfee.com" style={{ color: 'inherit', fontWeight: 600 }}>FodyFee</a>
          </p>
        </div>
      </footer>

      {/* ====== Product Options Modal ====== */}
      {selectedProduct && (
        <div className="ff-modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="ff-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ff-modal-header">
              <h3>{selectedProduct.name}</h3>
              <button className="ff-modal-close" onClick={() => setSelectedProduct(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="ff-modal-body">
              {selectedProduct.imageUrl && showProductImages && (
                <img src={selectedProduct.imageUrl} alt={selectedProduct.name} className="ff-modal-img" />
              )}
              {/* Product Rating Header */}
              {(() => {
                const pr = productRatings?.[selectedProduct.id];
                if (!pr || pr.totalReviews === 0) return null;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                    <StarDisplay rating={pr.averageRating} size={14} />
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>
                      {pr.averageRating.toFixed(1)}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#888' }}>
                      ({pr.totalReviews} review{pr.totalReviews === 1 ? '' : 's'})
                    </span>
                  </div>
                );
              })()}
              {getProductOptions(selectedProduct).map((opt) => (
                <div key={opt.id} className="ff-option-group">
                  <div className="ff-option-title">
                    {opt.name}
                    {opt.optionRequired && <span className="ff-required ml-1">*</span>}
                    {opt.allowMultipleSelection && (
                      <span className="ff-option-sub">Choose multiple</span>
                    )}
                  </div>
                  <div className="ff-option-choices">
                    {(opt.options || []).map((choice, idx) => {
                      const selected = isChoiceSelected(opt.id, idx);
                      return (
                        <button
                          key={idx}
                          className={`ff-choice-btn ${selected ? 'selected' : ''}`}
                          onClick={() =>
                            toggleOptionChoice(opt.id, idx, opt.allowMultipleSelection)
                          }
                        >
                          <span>{choice.name}</span>
                          {(choice.extraFee || 0) > 0 && (
                            <span className="ff-choice-extra">+{formatCurrency(choice.extraFee)}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Product Reviews (read-only, horizontal scrollable with lazy loading) */}
              {selectedProductReviews.length > 0 && (
                <div style={{ marginTop: 16, marginBottom: 8 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#555', marginBottom: 8 }}>
                    <i className="fas fa-comment-alt mr-1" style={{ color: primaryColor }}></i>
                    What others say
                  </div>
                  <div
                    ref={productReviewsScrollRef}
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 20) {
                        loadMoreProductReviews();
                      }
                    }}
                    style={{
                      display: 'flex',
                      gap: 10,
                      overflowX: 'auto',
                      paddingBottom: 8,
                      scrollSnapType: 'x mandatory',
                      WebkitOverflowScrolling: 'touch',
                    }}
                  >
                    {selectedProductReviews.map((rev) => (
                      <div
                        key={rev.id}
                        style={{
                          flex: '0 0 220px',
                          padding: '10px 12px',
                          background: '#f8f9fa',
                          borderRadius: 10,
                          border: '1px solid #eee',
                          scrollSnapAlign: 'start',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#333' }}>{rev.consumerName || 'Customer'}</span>
                            <StarDisplay rating={rev.rating} size={10} />
                          </div>
                          {rev.comment && (
                            <p style={{ fontSize: '0.8rem', color: '#555', margin: 0, lineHeight: 1.3 }}>{rev.comment}</p>
                          )}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#aaa', marginTop: 6 }}>
                          {rev.createdAt?.toDate ? rev.createdAt.toDate().toLocaleDateString() : new Date(rev.createdAt || 0).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                    {selectedProductReviewLoading && (
                      <div style={{ flex: '0 0 100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="fas fa-spinner fa-spin" style={{ color: '#ccc', fontSize: '1rem' }}></i>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedProduct.acceptSpecialRequirement && (
                <div className="ff-auth-field mt-3">
                  <label>Special Instructions</label>
                  <textarea
                    className="ff-form-control"
                    rows={2}
                    placeholder="Allergies, special requests..."
                    value={modalInstructions}
                    onChange={(e) => setModalInstructions(e.target.value)}
                    style={{ width: '100%', padding: 12, borderRadius: 10, border: '2px solid #eee', fontFamily }}
                  />
                </div>
              )}

              <div className="ff-modal-qty-row mt-3">
                <span className="ff-modal-qty-label">Quantity</span>
                <div className="ff-qty-control">
                  <button onClick={() => setModalQty((q) => Math.max(1, q - 1))}>-</button>
                  <span>{modalQty}</span>
                  <button onClick={() => setModalQty((q) => q + 1)}>+</button>
                </div>
              </div>

            </div>
            <div className="ff-modal-footer">
              <div className="ff-modal-total">
                <span>Total</span>
                <span style={{ color: primaryColor, fontWeight: 700 }}>
                  {formatCurrency(getModalItemTotal())}
                </span>
              </div>
              <button
                className="ff-btn ff-btn-primary ff-btn-block"
                style={{ background: primaryColor }}
                onClick={() => {
                  const opts = getProductOptions(selectedProduct);
                  for (const opt of opts) {
                    if (opt.optionRequired) {
                      const hasSel = selectedOptions.some((s) => s.optionId === opt.id);
                      if (!hasSel) {
                        Swal.fire('Required', `Please select an option for ${opt.name}`, 'warning');
                        return;
                      }
                    }
                  }
                  addToCart(selectedProduct, selectedOptions, modalQty, modalInstructions);
                }}
              >
                <i className="fas fa-plus mr-2"></i>Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== Cart Drawer ====== */}
      {cartOpen && (
        <div className="ff-drawer-overlay" onClick={() => setCartOpen(false)}>
          <div className="ff-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="ff-drawer-header">
              <h3><i className="fas fa-shopping-bag mr-2"></i>Your Cart</h3>
              <button className="ff-drawer-close" onClick={() => setCartOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="ff-drawer-body">
              {cart.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-shopping-basket fa-2x mb-2" style={{ opacity: 0.3 }}></i>
                  <p>Your cart is empty.</p>
                </div>
              ) : (
                cart.map((item, idx) => {
                  let unit = parseFloat(item.product.price) || 0;
                  item.options.forEach((sel) => {
                    const optDef = productOptions.find((o) => o.id === sel.optionId);
                    if (optDef && optDef.options && optDef.options[sel.choiceIndex]) {
                      unit += parseFloat(optDef.options[sel.choiceIndex].extraFee) || 0;
                    }
                  });
                  return (
                    <div key={idx} className="ff-cart-item">
                      <div className="ff-cart-item-info">
                        <span className="ff-cart-item-name">{item.product.name}</span>
                        {item.options.length > 0 && (
                          <span className="ff-cart-item-options">
                            {item.options
                              .map((sel) => {
                                const optDef = productOptions.find((o) => o.id === sel.optionId);
                                return optDef?.options?.[sel.choiceIndex]?.name || '';
                              })
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        )}
                        {item.instructions && (
                          <span className="ff-cart-item-instructions">Note: {item.instructions}</span>
                        )}
                        <span className="ff-cart-item-price">{formatCurrency(unit)} each</span>
                      </div>
                      <div className="ff-cart-item-actions">
                        <div className="ff-qty-control small">
                          <button onClick={() => updateCartQty(idx, -1)}>-</button>
                          <span>{item.qty}</span>
                          <button onClick={() => updateCartQty(idx, 1)}>+</button>
                        </div>
                        <button className="ff-btn-sm ff-btn-sm-danger" onClick={() => removeCartItem(idx)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {cart.length > 0 && (
              <div className="ff-drawer-footer">
                <div className="ff-cart-summary">
                  <div className="ff-summary-row">
                    <span>Subtotal</span>
                    <span>{formatCurrency(cartTotals.subtotal)}</span>
                  </div>
                  {cartTotals.taxRate > 0 && (
                    <div className="ff-summary-row">
                      <span>Tax ({cartTotals.taxRate}%)</span>
                      <span>{formatCurrency(cartTotals.tax)}</span>
                    </div>
                  )}
                  <div className="ff-summary-row total">
                    <span>Total</span>
                    <span>{formatCurrency(cartTotals.total)}</span>
                  </div>
                </div>
                <button
                  className="ff-btn ff-btn-primary ff-btn-block"
                  style={{ background: primaryColor }}
                  onClick={() => {
                    setCartOpen(false);
                    navigate(`/checkout/${slug}`, reservationContext ? { state: { reservationContext } } : undefined);
                  }}
                >
                  <i className="fas fa-credit-card mr-2"></i>Checkout
                </button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

// Sub-component: star display (read-only)
const StarDisplay = ({ rating, size = 12, color = '#ffc107' }) => {
  const r = Math.max(0, Math.min(5, rating || 0));
  return (
    <span style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`fas fa-star${s <= Math.round(r) ? '' : '-half-alt'}`}
          style={{ color: s <= Math.round(r) ? color : '#e0e0e0', fontSize: size }}
        ></i>
      ))}
    </span>
  );
};

// Sub-component for product list with layout variants
const ProductList = ({ products, productLayout, template, design, primaryColor, secondaryColor, br, showProductImages, fontFamily, openProductModal, formatCurrency, productRatings }) => {
  if (products.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="fas fa-utensils fa-2x mb-2" style={{ opacity: 0.3 }}></i>
        <p>No products in this category.</p>
      </div>
    );
  }

  if (productLayout === 'list') {
    return (
      <div className="ff-store-products-list">
        {products.map((product) => (
          <div
            key={product.id}
            className="ff-store-product-list-item"
            onClick={() => openProductModal(product)}
            style={{ borderBottom: `1px solid ${template === 'classic' ? '#e0e0e0' : '#eee'}`, padding: '14px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: template === 'classic' ? 500 : 600, fontSize: '1.05rem', fontFamily }}>{product.name}</div>
              {product.description && <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: 2, fontStyle: template === 'classic' ? 'italic' : 'normal' }}>{product.description}</div>}
              {(() => {
                const pr = productRatings?.[product.id];
                if (!pr || pr.totalReviews === 0) return null;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    <StarDisplay rating={pr.averageRating} size={11} />
                    <span style={{ fontSize: '0.75rem', color: '#888' }}>{pr.averageRating.toFixed(1)} ({pr.totalReviews})</span>
                  </div>
                );
              })()}
              {(product.optionIds?.length > 0 || product.acceptSpecialRequirement) && (
                <div style={{ fontSize: '0.75rem', color: secondaryColor, marginTop: 4 }}>Customizable</div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 16 }}>
              <div style={{ fontWeight: template === 'classic' ? 400 : 700, fontSize: '1.1rem', color: design.accentColor || primaryColor, fontFamily }}>
                {product.onSale && product.salePrice !== undefined ? (
                  <>
                    <span style={{ textDecoration: 'line-through', marginRight: 6, opacity: 0.6, fontSize: '0.9rem' }}>{formatCurrency(product.price)}</span>
                    {formatCurrency(product.salePrice)}
                  </>
                ) : formatCurrency(product.price)}
              </div>
              <button className="ff-add-btn" style={{ background: primaryColor }} onClick={(e) => { e.stopPropagation(); openProductModal(product); }}>
                <i className="fas fa-plus"></i>
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (productLayout === 'cards') {
    return (
      <div className="ff-store-products-grid" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {products.map((product) => (
          <div
            key={product.id}
            className="ff-store-product-card"
            onClick={() => openProductModal(product)}
            style={{
              background: template === 'classic' ? 'transparent' : '#fff',
              borderRadius: br,
              padding: 16,
              boxShadow: template === 'classic' ? 'none' : template === 'bistro' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 12px rgba(0,0,0,0.08)',
              border: template === 'classic' ? `1px solid ${secondaryColor}40` : '1px solid #f5f5f5',
              display: 'flex',
              gap: 14,
              alignItems: 'flex-start',
              cursor: 'pointer',
            }}
          >
            {showProductImages && product.imageUrl && (
              <img src={product.imageUrl} alt={product.name} style={{ width: template === 'bistro' ? 100 : 80, height: template === 'bistro' ? 100 : 80, objectFit: 'cover', borderRadius: template === 'bistro' ? '50%' : 10 }} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: template === 'classic' ? 500 : 700, fontSize: '1rem', marginBottom: 4, fontFamily }}>{product.name}</div>
              {product.description && <div style={{ fontSize: '0.85rem', opacity: 0.65, marginBottom: 8, fontStyle: template === 'classic' ? 'italic' : 'normal' }}>{product.description}</div>}
              {(() => {
                const pr = productRatings?.[product.id];
                if (!pr || pr.totalReviews === 0) return null;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                    <StarDisplay rating={pr.averageRating} size={12} />
                    <span style={{ fontSize: '0.75rem', color: '#888' }}>{pr.averageRating.toFixed(1)} ({pr.totalReviews})</span>
                  </div>
                );
              })()}
              {(product.optionIds?.length > 0 || product.acceptSpecialRequirement) && (
                <div style={{ fontSize: '0.75rem', color: secondaryColor, marginBottom: 8 }}>Customizable</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, color: design.accentColor || primaryColor, fontSize: '1.1rem', fontFamily }}>
                  {product.onSale && product.salePrice !== undefined ? (
                    <>
                      <span style={{ textDecoration: 'line-through', marginRight: 6, opacity: 0.6, fontSize: '0.9rem' }}>{formatCurrency(product.price)}</span>
                      {formatCurrency(product.salePrice)}
                    </>
                  ) : formatCurrency(product.price)}
                </div>
                <button className="ff-add-btn" style={{ background: primaryColor }} onClick={(e) => { e.stopPropagation(); openProductModal(product); }}>
                  <i className="fas fa-plus"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // grid (default)
  return (
    <div className="ff-store-products-grid">
      {products.map((product) => (
        <div
          key={product.id}
          className="ff-store-product-card"
          onClick={() => openProductModal(product)}
          style={{
            background: template === 'classic' ? 'transparent' : '#fff',
            borderRadius: br,
            overflow: 'hidden',
            boxShadow: template === 'classic' ? 'none' : template === 'bistro' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.06)',
            border: template === 'classic' ? `1px solid ${secondaryColor}40` : '1px solid #f0f0f0',
            cursor: 'pointer',
          }}
        >
          {showProductImages && product.imageUrl && (
            <div style={{ height: 140, backgroundImage: `url(${product.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: template === 'classic' ? `${br} ${br} 0 0` : 0 }} />
          )}
          <div style={{ padding: 14 }}>
            <div style={{ fontWeight: template === 'classic' ? 500 : 600, fontSize: '0.95rem', marginBottom: 4, fontFamily }}>{product.name}</div>
            {product.description && <div style={{ fontSize: '0.8rem', opacity: 0.65, marginBottom: 4, lineHeight: 1.3, fontStyle: template === 'classic' ? 'italic' : 'normal' }}>{product.description}</div>}
            {(() => {
              const pr = productRatings?.[product.id];
              if (!pr || pr.totalReviews === 0) return null;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                  <StarDisplay rating={pr.averageRating} size={11} />
                  <span style={{ fontSize: '0.7rem', color: '#888' }}>{pr.averageRating.toFixed(1)} ({pr.totalReviews})</span>
                </div>
              );
            })()}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, color: design.accentColor || primaryColor, fontSize: '1rem', fontFamily }}>
                {product.onSale && product.salePrice !== undefined ? (
                  <>
                    <span style={{ textDecoration: 'line-through', marginRight: 6, opacity: 0.6, fontSize: '0.85rem' }}>{formatCurrency(product.price)}</span>
                    {formatCurrency(product.salePrice)}
                  </>
                ) : formatCurrency(product.price)}
              </span>
              <button className="ff-add-btn" style={{ background: primaryColor }} onClick={(e) => { e.stopPropagation(); openProductModal(product); }}>
                <i className="fas fa-plus"></i>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Cover Photo Carousel with Hearts ─────────────────────────────────────────
const CoverPhotoCarousel = ({ storeId, images, user }) => {
  const [current, setCurrent] = useState(0);
  const [hearts, setHearts] = useState(() => images.map(() => 0));
  const [userHearted, setUserHearted] = useState(new Set());
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const db = getFirestore();
        const counts = await Promise.all(
          images.map((_, i) =>
            getDoc(doc(db, 'publicStores', storeId, 'coverHearts', String(i)))
              .then((d) => (d.exists() ? d.data().count || 0 : 0))
          )
        );
        setHearts(counts);

        if (user) {
          const voteDoc = await getDoc(doc(db, 'users', user.uid, 'coverHeartVotes', storeId));
          if (voteDoc.exists()) {
            const indices = voteDoc.data().heartedIndices || [];
            setUserHearted(new Set(indices.map((n) => Number(n))));
          }
        }
      } catch (e) {
        console.error('Failed to load cover hearts:', e);
      }
    };
    load();
  }, [storeId, images, user]);

  const toggleHeart = async (index, e) => {
    e.stopPropagation();
    if (!user) {
      Swal.fire({ icon: 'info', title: 'Sign in required', text: 'Please sign in to heart photos.' });
      return;
    }
    const db = getFirestore();
    const isHearted = userHearted.has(index);
    const delta = isHearted ? -1 : 1;

    try {
      await setDoc(doc(db, 'publicStores', storeId, 'coverHearts', String(index)), {
        count: increment(delta),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      await setDoc(doc(db, 'users', user.uid, 'coverHeartVotes', storeId), {
        heartedIndices: isHearted ? arrayRemove(index) : arrayUnion(index),
      }, { merge: true });

      setHearts((prev) => {
        const next = [...prev];
        next[index] = Math.max(0, next[index] + delta);
        return next;
      });
      setUserHearted((prev) => {
        const next = new Set(prev);
        if (isHearted) next.delete(index);
        else next.add(index);
        return next;
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops', text: err.message });
    }
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Swipe support
  useEffect(() => {
    const el = containerRef.current;
    if (!el || images.length <= 1) return;
    let startX = 0;
    const onStart = (e) => { startX = e.touches[0].clientX; };
    const onEnd = (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 50) {
        setCurrent((c) => {
          if (dx < 0) return Math.min(c + 1, images.length - 1);
          return Math.max(c - 1, 0);
        });
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
    };
  }, [images.length]);

  return (
    <>
      <div
        className="container"
        style={{ marginTop: 12, marginBottom: 8 }}
      >
        <div
          ref={containerRef}
          style={{
            display: 'flex',
            gap: 10,
            overflowX: images.length > 1 ? 'auto' : 'hidden',
            scrollSnapType: 'x mandatory',
            borderRadius: 12,
            paddingBottom: 4,
          }}
        >
          {images.map((url, i) => (
            <div
              key={i}
              onClick={() => openLightbox(i)}
              style={{
                flex: images.length > 1 ? '0 0 85%' : '0 0 100%',
                scrollSnapAlign: 'start',
                position: 'relative',
                borderRadius: 12,
                overflow: 'hidden',
                cursor: 'pointer',
                aspectRatio: '16 / 9',
              }}
            >
              <img
                src={url}
                alt={`Cover ${i + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                loading="lazy"
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'rgba(0,0,0,0.5)',
                  padding: '4px 10px',
                  borderRadius: 16,
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <button
                  onClick={(e) => toggleHeart(i, e)}
                  style={{ background: 'none', border: 'none', color: userHearted.has(i) ? '#ff1744' : '#fff', fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1 }}
                >
                  <i className={userHearted.has(i) ? 'fas fa-heart' : 'far fa-heart'}></i>
                </button>
                <span>{hearts[i]}</span>
              </div>
            </div>
          ))}
        </div>
        {images.length > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 8 }}>
            {images.map((_, i) => (
              <span
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: current === i ? '#FF5722' : '#ddd',
                  transition: 'background 0.2s',
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer' }}
          >
            <i className="fas fa-times"></i>
          </button>
          <div
            style={{
              maxWidth: '90vw',
              maxHeight: '80vh',
              position: 'relative',
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[lightboxIndex]}
              alt="Cover"
              style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8, objectFit: 'contain' }}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                marginTop: 12,
                color: '#fff',
              }}
            >
              <button
                onClick={(e) => toggleHeart(lightboxIndex, e)}
                style={{ background: 'none', border: 'none', color: userHearted.has(lightboxIndex) ? '#ff1744' : '#fff', fontSize: 24, cursor: 'pointer' }}
              >
                <i className={userHearted.has(lightboxIndex) ? 'fas fa-heart' : 'far fa-heart'}></i>
              </button>
              <span style={{ fontSize: 18, fontWeight: 600 }}>{hearts[lightboxIndex]}</span>
            </div>
            {images.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
                <button
                  onClick={() => setLightboxIndex((i) => Math.max(0, i - 1))}
                  disabled={lightboxIndex === 0}
                  style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '8px 14px', borderRadius: 8, cursor: 'pointer' }}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <span style={{ color: '#fff', fontSize: 14, alignSelf: 'center' }}>
                  {lightboxIndex + 1} / {images.length}
                </span>
                <button
                  onClick={() => setLightboxIndex((i) => Math.min(images.length - 1, i + 1))}
                  disabled={lightboxIndex === images.length - 1}
                  style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '8px 14px', borderRadius: 8, cursor: 'pointer' }}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StorePage;
